import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PermissionProtectedRoute from "../../aAdminTabTeam2/routes/PermissionProtectedRoute";
import { CreateProjectKeyDetailsPayload } from "../../api/services/project/model";
import {
  useCreateProjectKeyDetailsMutation,
  useGetProjectKeyDetailsById,
} from "../../api/services/project/projectApis";
import {
  MuiBox,
  MuiButtonSecondary,
  MuiButtonTertiary,
  MuiModal,
  MuiTypography,
  WarningConfirmationModal,
} from "../../components";
import AccessDenied from "../../components/accessDenied/AccessDenied";
import { InformationBoxWrapper } from "../../components/informationBox/InformationBox.style";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS, TIMEOUT_100 } from "../../helpers/constants";
import { PAGE_IDS, PAGE_URLS } from "../../helpers/constants/pageUrls";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  checkIsStatusCode403Forbidden,
  getValueOrBlank,
  isNotEmpty,
  scrollToTopContainer,
} from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { navigationDataSelector } from "../../redux/slices/appState/appStateSelector";
import {
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
} from "../../redux/slices/appState/appStateSlice";
import { updateProgramCreatedDate } from "../../redux/slices/program/programSlice";
import {
  childProgramNavigationSelector,
  keyDetailsSelector,
} from "../../redux/slices/project/projectSelector";
import {
  clearProjectDetails,
  clearStartChildProgramNavigation,
  saveKeyProjectDetails,
  updateProjectOpcoFieldsEditable,
} from "../../redux/slices/project/projectSlice";
import { CreateProjectFooter } from "./components/createProjectFooter/CreateProjectFooter";
import { CreateProjectGeneralDetail } from "./components/createProjectGeneralDetail/CreateProjectGeneralDetail";
import { CreateProjectInformationModal } from "./components/createProjectInformationModal/CreateProjectInformationModal";
import { CreateProjectKeyInput } from "./components/createProjectKeyInput/CreateProjectKeyInput";
import { CreateProjectTabs } from "./components/createProjectTabs/CreateProjectTabs";
import { ProjectInvoiceHistory } from "./components/invoiceHistory/ProjectInvoiceHistoryMain";
import { CreateProjectWrapper } from "./CreateProject.style";
import {
  ProjectKeyDetailsForm,
  projectKeyDetailsDefaultValue,
} from "./modelsAndSchemas/Models";
import { CreateProjectKeyDetailsSchema } from "./modelsAndSchemas/ValidationSchema";
import {
  createNewProjectHeaderSiteCoreKeys,
  keyProjectDetailsSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";

interface CreateProgramProps {
  isEditProject?: boolean;
  pageId?: string;
  pageUrl?: string;
  projectId?: string;
  tabValue?: number;
  isNavigatedFromProgram?: boolean;
}

const CreateProject: FC<CreateProgramProps> = ({
  pageId = PAGE_IDS.CREATE_PROJECT,
  isEditProject = false,
  projectId = "",
  tabValue = 0,
  isNavigatedFromProgram,
  pageUrl = PAGE_URLS.CREATE_PROJECT,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    pageTitle,
    commonValidationMessages,
    commonLabels,
    fetchLabel,
    fetchCommonLabel,
    fetchValidationMessage,
  } = useSiteCore();
  const [isProjectCreated, setIsProjectCreated] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] =
    useState(false);
  const [isTabFormDirty, setIsTabFormDirty] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const navigationData = useSelector(navigationDataSelector);
  const { displayToast } = useToast();
  const { isMobile } = useMediaQueries();
  const { id } = useParams();

  const projectKeyDetails = useSelector(keyDetailsSelector);
  const childProgramNavigation = useSelector(childProgramNavigationSelector);

  useEffect(() => {
    if (childProgramNavigation) {
      setIsProjectCreated(true);
    }
  }, [childProgramNavigation]);

  const {
    mutateAsync: addProjectKeyDetailsApi,
    isPending: isAddProjectKeyDetailsPending,
    isError: isAddProjectKeyDetailsError,
    isSuccess: isProjectKeyDetailsSuccess,
    data: createdProjectData,
  } = useCreateProjectKeyDetailsMutation();

  const {
    data: projectKeyDetailsResponse,
    isSuccess: isGetKeyProjectSuccess,
    isFetching: isGetKeyProjectPending,
    isError: isGetKeyProjectError,
    error: getKeyProjectError,
    refetchWithParams: refetchProjectKeyDetail,
  } = useGetProjectKeyDetailsById();

  const finalProjectId =
    createdProjectData?.id ?? projectKeyDetails?.id ?? projectId;

  useEffect(() => {
    if (isGetKeyProjectError && getKeyProjectError) {
      if (checkIsStatusCode403Forbidden(getKeyProjectError as AxiosError)) {
        setShowAccessDenied(true);
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }
      dispatch(toggleLoader({ isVisible: false }));
    } else {
      setShowAccessDenied(false);
    }
  }, [isGetKeyProjectError, getKeyProjectError]);

  useEffect(() => {
    if (isAddProjectKeyDetailsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isAddProjectKeyDetailsError]);

  const isLoading = isAddProjectKeyDetailsPending || isGetKeyProjectPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isProjectKeyDetailsSuccess) {
      displayToast(
        fetchLabel(
          keyProjectDetailsSiteCoreKeys.keyProjectDetails,
          keyProjectDetailsSiteCoreKeys.projectCreatedMessage,
        ),
      );
      setShowInformationModal(false);
      setIsProjectCreated(true);
      scrollToTopContainer();
    }
  }, [isProjectKeyDetailsSuccess]);

  const refetchKeyDetailsWithOffice = () => {
    refetchProjectKeyDetail(createdProjectData!.id);
  };

  useEffect(() => {
    if (isNotEmpty(createdProjectData)) {
      refetchKeyDetailsWithOffice();
    }
  }, [createdProjectData]);

  useEffect(() => {
    if (
      isEditProject &&
      isNotEmpty(projectId) &&
      projectKeyDetails?.id !== projectId
    ) {
      setShowAccessDenied(false);
      refetchProjectKeyDetail(projectId);
      setIsProjectCreated(true);
    }
  }, [isEditProject, projectId]);

  useEffect(() => {
    if (isGetKeyProjectSuccess) {
      dispatch(
        updateProgramCreatedDate(projectKeyDetailsResponse?.createdDate),
      );
    }
  }, [isGetKeyProjectSuccess]);

  const methods = useForm<ProjectKeyDetailsForm>({
    mode: "all",
    defaultValues: projectKeyDetailsDefaultValue,
    resolver: yupResolver(
      CreateProjectKeyDetailsSchema(
        commonValidationMessages,
        fetchValidationMessage,
      ),
    ),
  });

  const { handleSubmit, formState, getValues } = methods;

  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  useEffect(() => {
    const isShowConfirmationPopup =
      (!isEditProject && formState.isDirty) || isEditProject;
    dispatch(setIsDataModifiedCurrentPage(isShowConfirmationPopup));
  }, [formState]);

  const handleSaveProjectKeyDetailButton = () => {
    if (!isProjectCreated) {
      handleSubmit(() => setShowInformationModal(true))();
    }
  };

  const handleNavigateBack = () => {
    location.state?.from === PAGE_URLS.SEARCH_PROJECT ||
    location.pathname.includes(PAGE_URLS.EDIT_PROJECT)
      ? navigate(-1)
      : navigate(PAGE_URLS.DASHBOARD);
  };

  const getAddKeyProjectDetailsPayload = (): CreateProjectKeyDetailsPayload => {
    const {
      projectName,
      client: clientId,
      productLines: productLineId,
      primaryProductLine,
      poNumbers,
      clientBusinessUnit,
      clientCostCenter: clientCostCentre,
      projectFramework: projectFrameworkId,
      primaryDeliveryOffice,
      salesOfficeId,
      dossierNumber,
      actualClientId,
    } = getValues();

    return {
      projectName,
      clientId,
      productLineId,
      primaryProductLineId: primaryProductLine,
      purchaseOrderId: isNotEmpty(poNumbers) ? poNumbers : [],
      clientBusinessUnit,
      clientCostCentre,
      projectFrameworkId,
      primaryDeliveryOfficeId: getValueOrBlank(primaryDeliveryOffice),
      salesOfficeId: getValueOrBlank(salesOfficeId),
      dossierNumber,
      actualClientId,
    };
  };

  const handleSaveProjectKeyDetails = () => {
    addProjectKeyDetailsApi(getAddKeyProjectDetailsPayload());
  };

  useEffect(() => {
    // dispatch(updateProgramStatus(projectKeyDetails?.projectStatus!));
    if (isNotEmpty(projectKeyDetailsResponse)) {
      dispatch(
        saveKeyProjectDetails({
          ...projectKeyDetailsResponse,
          id: isEditProject ? projectId : (createdProjectData?.id ?? ""),
        }),
      );
      dispatch(
        updateProjectOpcoFieldsEditable(
          !!projectKeyDetailsResponse.areOpcoFieldsEditable,
        ),
      );
    }
  }, [projectKeyDetailsResponse]);

  // Check country-level access from isEditable field
  useEffect(() => {
    if (isGetKeyProjectSuccess && isNotEmpty(projectKeyDetailsResponse)) {
      setShowAccessDenied(projectKeyDetailsResponse?.isEditable !== true);
    }
  }, [isGetKeyProjectSuccess, projectKeyDetailsResponse?.isEditable]);

  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };

  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    dispatch(setIsDataModifiedCurrentPage(false));
    dispatch(clearStartChildProgramNavigation()); // Clear Child Program Navigation state on sidebar navigation
    dispatch(clearProjectDetails()); // Clear Project Details on sidebar navigation
    setTimeout(() => {
      navigate(navigationData.navigationPath);
    }, TIMEOUT_100);
    setShowExitConfirmationModal(false);
  };

  const handleBackButtonPressed = () => {
    handleBackFromProjectTabs(isTabFormDirty);
  };
  const handleConfirmationModalPrimaryPressed = () => {
    setShowDiscardConfirmationModal(false);
    goBack();
  };

  const handleBackFromProjectTabs = (shouldShowConfirmation?: boolean) => {
    if (shouldShowConfirmation) {
      setShowDiscardConfirmationModal(true);
    } else {
      goBack();
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const handleCallBackTopBackButton = (isDirty: boolean) => {
    setIsTabFormDirty(isDirty);
  };

  if (showAccessDenied && isEditProject) {
    return <AccessDenied />;
  }

  return (
    <PermissionProtectedRoute
      hasCountryLevelAccess={
        projectKeyDetailsResponse?.isEditable || !isEditProject
      }
    >
      <CreateProjectWrapper data-testid="create-project">
        {isNavigatedFromProgram && (
          <MuiButtonTertiary
            data-testid={`${pageId}-back`}
            onClick={handleBackButtonPressed}
          >
            <ArrowBackIosIcon />
            {commonLabels.back}
          </MuiButtonTertiary>
        )}
        <FormProvider {...methods}>
          <InformationBoxWrapper className="information-box">
            <MuiBox className="line" />
            <MuiTypography variant="h1Bold" className="text" component="h1">
              {isEditProject
                ? fetchLabel(
                    createNewProjectHeaderSiteCoreKeys.createNewProjectHeader,
                    createNewProjectHeaderSiteCoreKeys.editProjectHeader,
                  )
                : pageTitle}
            </MuiTypography>
            {isEditProject && !isMobile && (
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.PROJECT_VIEW_INVOICE_HISTORY}
              >
                <MuiBox className="view-invoice-btn">
                  <MuiButtonSecondary
                    onClick={() => setShowInvoiceHistory(true)}
                  >
                    {fetchLabel(
                      createNewProjectHeaderSiteCoreKeys.createNewProjectHeader,
                      createNewProjectHeaderSiteCoreKeys.viewInvoiceHistoryButton,
                    )}
                  </MuiButtonSecondary>
                </MuiBox>
              </PermissionWrapper>
            )}
          </InformationBoxWrapper>
          {isEditProject && isMobile && (
            <MuiBox className="view-invoice-btn">
              <MuiButtonSecondary onClick={() => setShowInvoiceHistory(true)}>
                {fetchLabel(
                  createNewProjectHeaderSiteCoreKeys.createNewProjectHeader,
                  createNewProjectHeaderSiteCoreKeys.viewInvoiceHistoryButton,
                )}
              </MuiButtonSecondary>
            </MuiBox>
          )}
          {!(isProjectCreated || projectKeyDetails?.id) ? (
            <CreateProjectKeyInput pageId={pageId} />
          ) : (
            <MuiBox>
              <CreateProjectGeneralDetail
                pageId={pageId}
                projectId={finalProjectId}
                refetchProjectKeyDetailApi={refetchProjectKeyDetail}
              />
              <CreateProjectTabs
                pageId={pageId}
                handleNavigateBack={handleNavigateBack}
                projectId={finalProjectId}
                isEditProject={isEditProject}
                tabValue={tabValue}
                pageUrl={pageUrl}
                isNavigatedFromProgram={isNavigatedFromProgram}
                handleDirtyTabCallBack={handleCallBackTopBackButton}
              />
            </MuiBox>
          )}

          {!(isProjectCreated || projectKeyDetails?.id) && (
            <CreateProjectFooter
              pageId={pageId}
              onConfirmClick={handleSaveProjectKeyDetailButton}
              handleNavigateBack={handleNavigateBack}
              confirmButtonText={commonLabels.continue}
              isFiledUpdated={formState.isDirty}
            />
          )}
          {showInformationModal && (
            <CreateProjectInformationModal
              showModal={showInformationModal}
              handleModalClose={() => setShowInformationModal(false)}
              handlePrimaryButtonClick={handleSaveProjectKeyDetails}
            />
          )}

          {showInvoiceHistory && (
            <MuiModal
              hasLargeContent
              open={showInvoiceHistory}
              onClose={() => setShowInvoiceHistory(false)}
              title={fetchCommonLabel(commonSiteCoreKeys.invoiceHistory)}
              primaryButtonTitle={commonLabels.ok}
              onPrimaryButtonClick={() => setShowInvoiceHistory(false)}
            >
              <ProjectInvoiceHistory
                projectId={id || ""}
                isViewOnly={!isEditProject}
              />
            </MuiModal>
          )}
        </FormProvider>
        {showDiscardConfirmationModal && (
          <WarningConfirmationModal
            show={showDiscardConfirmationModal}
            modalTitle={commonLabels.confirmation}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
            onClose={() => setShowDiscardConfirmationModal(false)}
            onPrimaryButtonClick={handleConfirmationModalPrimaryPressed}
            labelContent={commonLabels.discardChangesText}
          />
        )}
        {showExitConfirmationModal && (
          <WarningConfirmationModal
            labelContent={commonLabels.leavingPageActionText}
            show={showExitConfirmationModal}
            onClose={handleExitConfirmationModalClose}
            onPrimaryButtonClick={handleExitConfirmationModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </CreateProjectWrapper>
    </PermissionProtectedRoute>
  );
};

export default CreateProject;
